import React from "react";
import { ResponsiveModal, useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { UserSelect } from "@/components/shared/UserSelect";
import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { SavedView, SavedViewVisibility } from "@/lib/types/saved-views";
import type { useUpdateSavedView } from "@/lib/hooks/api/saved-views";

export interface ShareViewModalProps {
  selectedView: SavedView | undefined;
  resource: string;
  shareVisibility: SavedViewVisibility;
  onShareVisibilityChange: (visibility: SavedViewVisibility) => void;
  shareUserIds: string[];
  onShareUserIdsChange: (userIds: string[]) => void;
  updateView: ReturnType<typeof useUpdateSavedView>;
  entityName?: string;
  validateUserAccess?: (userId: string) => boolean;
  onSave?: () => void;
  globalModalId?: string; // Allow custom modal ID for different contexts
}

export function ShareViewModal({
  selectedView,
  resource,
  shareVisibility,
  onShareVisibilityChange,
  shareUserIds,
  onShareUserIdsChange,
  updateView,
  entityName,
  validateUserAccess,
  onSave,
  globalModalId = "share-view", // Default to "share-view" for backwards compatibility
}: ShareViewModalProps) {
  const { closeModal } = useGlobalModalStack();
  const isLoading = updateView.isPending;
  const error = updateView.error;
  const isDisabled = !selectedView || isLoading;

  return (
    <ResponsiveModal
      globalModalId={globalModalId}
      title="Share View"
      description="Choose visibility and optionally share with specific users."
      maxWidth="md"
    >
      <div className="space-y-6">
        {/* Visibility Section */}
        <div className="space-y-3">
          <div>
            <Label className="text-sm font-semibold">Visibility</Label>
            <p className="text-xs text-muted-foreground mt-1">
              Control who can access this view
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Button
              type="button"
              variant={shareVisibility === "private" ? "default" : "outline"}
              onClick={() => onShareVisibilityChange("private")}
              disabled={isLoading}
              className="text-sm"
            >
              Private
            </Button>
            <Button
              type="button"
              variant={shareVisibility === "shared" ? "default" : "outline"}
              onClick={() => onShareVisibilityChange("shared")}
              disabled={isLoading}
              className="text-sm"
            >
              Shared
            </Button>
            <Button
              type="button"
              variant={shareVisibility === "public" ? "default" : "outline"}
              onClick={() => onShareVisibilityChange("public")}
              disabled={isLoading}
              className="text-sm"
            >
              Public
            </Button>
          </div>
        </div>

        {/* Shared Users Section */}
        {shareVisibility === "shared" && (
          <div className="space-y-3 border-t pt-4">
            <div>
              <Label className="text-sm font-semibold">Share with users</Label>
              <p className="text-xs text-muted-foreground mt-1">
                {shareUserIds.length === 0
                  ? "No users selected yet"
                  : `Shared with ${shareUserIds.length} user${shareUserIds.length !== 1 ? "s" : ""}`}
              </p>
            </div>

            {/* User Access Validation Warning */}
            {shareUserIds.length > 0 && (
              <Alert className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/40">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-sm text-yellow-800 dark:text-yellow-200">
                  Ensure selected users have access to the {entityName || "resource"} before
                  sharing.
                </AlertDescription>
              </Alert>
            )}

            {/* User Badges */}
            {shareUserIds.length > 0 && (
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <div className="flex flex-wrap gap-2">
                  {shareUserIds.map((uid) => {
                    const hasAccess = validateUserAccess ? validateUserAccess(uid) : true;
                    return (
                      <Badge
                        key={uid}
                        variant="secondary"
                        className={`flex items-center gap-2 px-3 py-1 ${
                          !hasAccess ? "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/40" : ""
                        }`}
                      >
                        <span className="truncate max-w-[120px] text-xs">{uid}</span>
                        {!hasAccess && <AlertCircle className="h-3 w-3 text-red-600" />}
                        <button
                          type="button"
                          className="text-xs hover:underline disabled:opacity-50 disabled:cursor-not-allowed ml-1"
                          onClick={() =>
                            onShareUserIdsChange(shareUserIds.filter((id) => id !== uid))
                          }
                          disabled={isLoading}
                          aria-label={`Remove ${uid}`}
                        >
                          ✕
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add User Section */}
            <div className="space-y-2">
              <Label htmlFor="user-select" className="text-xs font-medium">
                Add more users
              </Label>
              <UserSelect
                currentUserId={undefined}
                onUserSelect={async (userId: string) => {
                  const newIds = shareUserIds.includes(userId)
                    ? shareUserIds
                    : [...shareUserIds, userId];
                  onShareUserIdsChange(newIds);
                }}
                placeholder="Search and add user..."
                showUnassigned={false}
                showAllUsers={false}
                disabled={isLoading}
              />
            </div>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="border-l-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              {error instanceof Error
                ? error.message
                : "Failed to update view sharing settings"}
            </AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button
            variant="outline"
            onClick={() => closeModal(globalModalId)}
            disabled={isLoading}
            className="text-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (onSave) {
                onSave();
              } else if (selectedView) {
                updateView.mutate(
                  {
                    id: selectedView.id,
                    resource,
                    updates: {
                      visibility: shareVisibility,
                      shared_with_user_ids:
                        shareVisibility === "shared" ? shareUserIds : [],
                    },
                  },
                  { onSuccess: () => closeModal(globalModalId) }
                );
              }
            }}
            disabled={isDisabled}
            aria-busy={isLoading}
            className="text-sm"
            title={isDisabled ? "Saving..." : "Save sharing settings"}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}
